import { useEffect, useState } from 'react';
import { Link } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { clientTime, shortDateTime } from '../../shared/dateTime';
import { plural } from '../../shared/plural';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { counterLabel, releasePlace, releaseTitle, releaseWhen } from './fleetScheduleRules';
import {
  fetchFleetMatches,
  type FleetCounters,
  type FleetMatches,
  type FleetMatchingState,
  type FleetPlanRow,
} from './vehiclePlanApi';

/**
 * Części harmonogramu floty wg makiety FTL p. Mariusza — Etap 1
 * (15.10.2026, pkt 9.1; przełączniki `fleetScheduleMockup`
 * i `fleetMatchingOrders`). Wyliczenia robi API (`fleet-mockup.ts`,
 * `fleet-matches.service.ts`); tu tylko pokazanie.
 */

/** Liczniki nad osią: wolne teraz / za 6 / 12 / 24 h, z wariantem, wyłączone. */
export const FleetCountersBar = ({ counters }: { counters: FleetCounters }) => {
  const cells: Array<{ key: string; label: string; value: number; hint: string }> = [
    ...counters.free.map((f) => ({
      key: `free-${f.hours}`,
      label: counterLabel(f.hours),
      value: f.count,
      hint: `z ${counters.total}`,
    })),
    { key: 'variant', label: 'Z wariantem', value: counters.withVariant, hint: 'wstępnie wskazane' },
    {
      key: 'out',
      label: 'Wyłączone z planowania',
      value: counters.outOfPlanning,
      hint: 'wg kartoteki TMS',
    },
  ];
  return (
    <Box
      data-testid="fleet-counters"
      title="Wolne = pojazd wchodzi w grę (kartoteka TMS) i w tej chwili nie zajmuje go żadne obsadzenie (szacowana zajętość). Liczone z pojazdów po filtrach, przed filtrem „Pokaż”."
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}
    >
      {cells.map((c) => (
        <Box
          key={c.key}
          data-testid={`fleet-counter-${c.key}`}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.25, minWidth: 96 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {c.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {c.value}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              {c.hint}
            </Typography>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/** Żetony przy aucie: alert przy obsadzeniu, bez kierowcy w kartotece. */
export const VehicleMarkers = ({ v }: { v: FleetPlanRow }) => {
  const m = v.markers;
  if (!m || (m.alerts === 0 && !m.noDriver)) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
      {m.alerts > 0 ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<WarningAmberIcon />}
          data-testid={`fleet-marker-alert-${v.id}`}
          title="Niepotwierdzone alerty przy obsadzeniach (zmiana w TMS, przeplanowanie…) — potwierdza się na karcie zlecenia."
          label={plural(m.alerts, 'alert', 'alerty', 'alertów')}
          sx={{ height: 18, fontSize: '0.68rem' }}
        />
      ) : null}
      {m.noDriver ? (
        <Chip
          size="small"
          variant="outlined"
          data-testid={`fleet-marker-nodriver-${v.id}`}
          title="W kartotece TMS pojazd nie ma przypisanego kierowcy."
          label="bez kierowcy"
          sx={{ height: 18, fontSize: '0.68rem' }}
        />
      ) : null}
    </Box>
  );
};

/** Komórka „Wolny od · gdzie”. */
export const ReleaseCell = ({ v, now }: { v: FleetPlanRow; now: Date }) => {
  if (v.usable === false) {
    return (
      <Typography variant="caption" color="text.secondary" title={v.outOfPlanningReason ?? undefined}>
        —
      </Typography>
    );
  }
  const when = releaseWhen(v.release, now);
  return (
    <Box title={releaseTitle(v.release, v.outOfPlanningReason)} data-testid={`fleet-release-${v.id}`}>
      <Typography variant="body2" sx={{ lineHeight: 1.3 }} noWrap>
        {when}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {releasePlace(v.release)}
      </Typography>
    </Box>
  );
};

/** Komórka „Pasujące” — liczba z przeliczenia w tle. */
export const MatchingCell = ({
  v,
  matching,
  onOpen,
}: {
  v: FleetPlanRow;
  matching: FleetMatchingState | undefined;
  onOpen: () => void;
}) => {
  if (v.usable === false) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        title={v.outOfPlanningReason ?? undefined}
        data-testid={`fleet-matching-${v.id}`}
      >
        —
      </Typography>
    );
  }
  const m = v.matching;
  if (!m) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        title={matching?.error ?? 'Liczę w tle — odświeży się samo.'}
        data-testid={`fleet-matching-${v.id}`}
      >
        {matching?.status === 'failed' ? '—' : 'liczę…'}
      </Typography>
    );
  }
  if (m.count === null) {
    return (
      <Typography variant="caption" color="text.secondary" title={m.refusal ?? undefined} data-testid={`fleet-matching-${v.id}`}>
        —
      </Typography>
    );
  }
  return (
    <Button
      size="small"
      variant="text"
      onClick={onOpen}
      data-testid={`fleet-matching-${v.id}`}
      title={`Zlecenia bez auta z załadunkiem w ciągu ${matching?.horizonDays ?? 7} dni, które rekomendacje pokazałyby dla tego pojazdu${
        matching?.computedAt ? ` (przeliczono ${clientTime(matching.computedAt)})` : ''
      }. Kliknij, żeby zobaczyć listę.`}
      sx={{ minWidth: 0, px: 0.5, fontWeight: 600 }}
    >
      {m.count}
    </Button>
  );
};

/** Opis stanu przeliczenia pod osią. */
export function matchingNote(matching: FleetMatchingState | undefined): string | null {
  if (!matching) return null;
  const base = `Kolumna „Pasujące” — zlecenia bez auta z załadunkiem w ciągu ${matching.horizonDays} dni, które rekomendacje „Zlecenia dla tego pojazdu” pokazałyby dla pojazdu (ta sama reguła), liczone w tle dla całej floty z dolotem szacunkiem.`;
  if (matching.status === 'pending') return `${base} Pierwsze przeliczenie trwa — liczby pojawią się same.`;
  if (matching.status === 'failed') return `${base} ${matching.error ?? 'Przeliczenie się nie udało.'}`;
  const at = matching.computedAt ? ` Przeliczono o ${clientTime(matching.computedAt)}.` : '';
  return matching.status === 'refreshing' ? `${base}${at} Trwa odświeżanie.` : `${base}${at}`;
}

const verdict = (v: boolean | null) => (v === null ? 'brak danych' : v ? 'tak' : 'nie');

/** Okno luki: pasujące zlecenia auta z załadunkiem w luce (z przeliczenia w tle). */
export const FleetMatchesDialog = ({
  target,
  onClose,
}: {
  target: { carId: number; label: string; from?: string; until?: string | null } | null;
  onClose: () => void;
}) => {
  const [data, setData] = useState<FleetMatches | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    fetchFleetMatches(target.carId, { from: target.from, until: target.until })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(toErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const range = target?.from
    ? `od ${shortDateTime(target.from)}${target.until ? ` do ${shortDateTime(target.until)}` : ''}`
    : 'cały horyzont rekomendacji';
  return (
    <Dialog open={target !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Zlecenia pasujące do luki — {target?.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Załadunek {range}.
          {data?.computedAt ? ` Przeliczono o ${clientTime(data.computedAt)}` : ''}
          {data?.computedAt ? ' (w tle, dolot szacunkiem — bez kosztu i marży).' : ''}
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!data && !error ? <CircularProgress size={20} /> : null}
        {data && data.refusal ? <Alert severity="info">{data.refusal}</Alert> : null}
        {data && !data.refusal && data.status === 'pending' ? (
          <Alert severity="info">Pierwsze przeliczenie trwa — spróbuj za chwilę.</Alert>
        ) : null}
        {data && !data.refusal && data.status !== 'pending' && data.orders.length === 0 ? (
          <Typography variant="body2" data-testid="fleet-matches-empty">
            Brak pasujących zleceń z załadunkiem w tej luce.
          </Typography>
        ) : null}
        {data && data.orders.length > 0 ? (
          <Table size="small" data-testid="fleet-matches-table">
            <TableHead>
              <TableRow>
                <TableCell>Zlecenie</TableCell>
                <TableCell>Relacja</TableCell>
                <TableCell>Załadunek</TableCell>
                <TableCell align="right">Dolot</TableCell>
                <TableCell>Zdąży</TableCell>
                <TableCell>Dowiezie</TableCell>
                <TableCell>Uwagi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.map((o) => (
                <TableRow key={o.userOfferId}>
                  <TableCell>
                    <Link to={`/user-offers/${o.userOfferId}/show`}>{o.externalId ?? `#${o.userOfferId}`}</Link>
                    {o.alreadyCandidate ? (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (już wariant)
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {o.from} → {o.to}
                  </TableCell>
                  <TableCell>{shortDateTime(o.fromDate)}</TableCell>
                  <TableCell align="right" title="Szacunek: linia prosta × mnożnik terenowy">
                    {o.accessKm} km
                  </TableCell>
                  <TableCell>{verdict(o.onTime)}</TableCell>
                  <TableCell>{verdict(o.delivery)}</TableCell>
                  <TableCell>{o.warnings > 0 ? plural(o.warnings, 'ostrzeżenie', 'ostrzeżenia', 'ostrzeżeń') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </DialogContent>
      <DialogActions>
        {target ? (
          <Typography variant="body2" sx={{ mr: 'auto', pl: 1 }}>
            <Link to={`/cars/${target.carId}/show`}>Pełne rekomendacje na karcie pojazdu</Link>
          </Typography>
        ) : null}
        <Button onClick={onClose} size="small">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};
